import { useState, useEffect } from "react";
import { Form } from "antd";

import { CustomNotification } from "../../util/CustomNotification";
import useGetOneMasterList from "../../hooks/useGetOneMasterList";
import { ModuleAndIdMapping, Modules } from "../../constants/DatabaseConstants/MasterConstant";
import useValue from "../../hooks/useValue";
import { createConcurrentRequest } from "../../services/concurrentRequest";
import * as commonAPI from "../../services/commonServices";

import type FixMeLater from "../../shared/types/fixMeLater.type";
import type { ConcurrentRequestData } from "../../shared/interface/concurrentRequest.interface";

export const useCreateConcurrentRequest = () => {
  const [form] = Form.useForm();
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [locationList, setLocationList] = useState<FixMeLater>([]);
  const { value: saving, handleEnableValue: handleEnableSaving, handleDisableValue: handleDisableSaving } = useValue(false);
  const { loading: customerListLoading, masterList: customerMasterList } = useGetOneMasterList(ModuleAndIdMapping[Modules.CUSTOMER_MASTER]);

  /* HANDLERS */
  const handleSubmit = async (data: ConcurrentRequestData) => {
    try {
      handleEnableSaving();

      const selectedCustomer = customerMasterList?.find((customer) => customer?.id === data?.customerId);

      const booking = data?.bookingKeyFobs?.split(",")?.map(Number) || [];
      const retrieval = data?.retrievalKeyFobs?.split(",")?.map(Number) || [];

      if (booking.length === 0 && retrieval.length === 0) {
        CustomNotification.showErrorMessage("Either booking or retrieval key fobs must be provided.");
        return;
      }

      const requestData = {
        customerName: selectedCustomer?.customer_name,
        locationName: data?.locationName,
        booking,
        retrieval
      };

      const response = await createConcurrentRequest(requestData);

      CustomNotification.showSuccessMessage(response?.message);
      form.resetFields();
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      handleDisableSaving();
    }
  };

  const handleChangeCustomer = (id: number | null) => {
    setCustomerId(id);
  };

  /* EFFECT */
  useEffect(() => {
    const fetchLocationList = async (customerId: number) => {
      try {
        const response = await commonAPI.getLocationListByCustomerId(customerId);
        setLocationList(response?.data);
      } catch (error) {
        CustomNotification.showErrorMessage(error);
      }
    };
    if (customerId) fetchLocationList(customerId);
  }, [customerId]);

  return {
    form,
    saving,
    loading: customerListLoading,
    handleSubmit,
    handleChangeCustomer,
    locationList,
    customerId,
    customerMasterList
  };
};
